/**
 * AmazonPasteDialog - メモに貼り付けられた Amazon URL を確認して商品カードに変換するダイアログ。
 *
 * NotionEditor の paste ハンドラから `url` を渡され、
 * - 「変換する」でスクレイピング API を叩き商品スナップショットを取得 → `onConfirm` で親に返す
 * - 「そのまま貼り付ける」で `onCancel` を呼び URL テキストのまま挿入
 * - 変換中はローディング表示、失敗時は toast
 */

import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { fetchAmazonProduct } from '@/api/services/memo'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'

type ConfirmAttrs = {
  asin: string | null
  url: string
  title: string
  image: string | null
  price: string | null
  brand: string | null
  rating: number | null
  reviewCount: number | null
  fetchedAt: string
}

type Props = {
  /** 表示対象 URL。`null` のときダイアログは閉じている */
  url: string | null
  /** URL 挿入 (変換キャンセル) */
  onCancel: () => void
  /** 商品カード挿入 (変換確定) */
  onConfirm: (attrs: ConfirmAttrs) => void
}

export const AmazonPasteDialog = ({ url, onCancel, onConfirm }: Props) => {
  const [isFetching, setIsFetching] = useState(false)

  // 新しい URL が渡されたら fetching をリセットする
  useEffect(() => {
    if (!url) setIsFetching(false)
  }, [url])

  const handleConvert = async () => {
    if (!url || isFetching) return
    setIsFetching(true)
    try {
      const snap = await fetchAmazonProduct(url)
      onConfirm({
        asin: snap.asin,
        url: snap.url,
        title: snap.title,
        image: snap.image,
        price: snap.price,
        brand: snap.brand,
        rating: snap.rating,
        reviewCount: snap.review_count,
        fetchedAt: snap.fetched_at,
      })
    } catch (e: unknown) {
      const message =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? '商品情報の取得に失敗しました'
      toast.error(message)
      setIsFetching(false)
    }
  }

  return (
    <Dialog
      open={url !== null}
      onOpenChange={(open) => {
        if (!open && !isFetching) onCancel()
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Amazon 商品カードに変換しますか?</DialogTitle>
        </DialogHeader>
        <div className="text-sm text-muted-foreground break-all">{url}</div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel} disabled={isFetching}>
            そのまま貼り付ける
          </Button>
          <Button onClick={handleConvert} disabled={isFetching}>
            {isFetching ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                取得中…
              </>
            ) : (
              '変換する'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
